import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import {
  ArrowLeft,
  Save,
  Building2,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Layers,
  Home,
  Plus,
  Minus,
  Calculator,
  FileText,
  CheckCircle2,
  AlertCircle,
  X,
  Mountain,
  Clock,
  Trees,
  Repeat,
  Trash2,
  Orbit,
  Globe,
  Info,
  Sparkles,
  MapPin,
  Sun,
  Moon
} from "lucide-react";
import { TimelineEstimateWidget } from "../../components/calculators/TimelineEstimateWidget";

const SALESPEOPLE = ["Jordan", "Julian", "Cole", "Bernard"];

const PROJECT_SIZES = [
  { value: "small", label: "Small Project", license: 1500 },
  { value: "medium", label: "Mid-size Project", license: 3000 },
  { value: "large", label: "Large Tower", license: 5000 }
];

// Exterior Modeling Complexity Tiers
const COMPLEXITY_TIERS = [
  { value: "small", label: "Small", description: "Cottage, small home" },
  { value: "medium", label: "Medium", description: "Townhome, mid-rise" },
  { value: "large", label: "Large", description: "High-rise tower" },
  { value: "xl", label: "XL", description: "Luxury tower, resort" }
];

// Site Environment Options
const SITE_ENVIRONMENTS = [
  { value: "none", label: "None", description: "No site modeling needed" },
  { value: "simple", label: "Simple", description: "Minimal roads, basic landscape" },
  { value: "standard", label: "Standard", description: "Roads, sidewalks, landscape" },
  { value: "complex", label: "Complex", description: "Pools, waterfront, terrain" },
  { value: "masterplan", label: "Masterplan", description: "Golf course, marina, large community" }
];

// Default rates (fallback)
const DEFAULT_EXT_RATES = {
  small: 12000,
  medium: 15000,
  large: 18000,
  xl: 25000,
  replication: 500,
  site_simple: 2000,
  site_standard: 5000,
  site_complex: 10000,
  site_masterplan: 15000,
  reality_capture: 8500
};

// Coverage calculation logic
function calculateCoverage(floorsCovered, uniqueFloorplates, repeatFloorplates) {
  const totalCovered = uniqueFloorplates + repeatFloorplates;
  const gap = floorsCovered - totalCovered;
  
  let status, statusType;
  if (gap === 0) {
    status = "All floors accounted for";
    statusType = "success";
  } else if (gap > 0) {
    status = `Missing ${gap} floor${gap > 1 ? 's' : ''} of coverage`;
    statusType = "warning";
  } else {
    status = `Over-allocated by ${Math.abs(gap)} floor${Math.abs(gap) > 1 ? 's' : ''}`;
    statusType = "warning";
  }
  
  return { totalCovered, gap, status, statusType };
}

// Create empty structure row
function createEmptyStructure() {
  return {
    id: crypto.randomUUID(),
    name: "",
    complexity: "medium",
    uniqueCount: 1,
    repeatCount: 0,
    notes: ""
  };
}

export default function VexosCalculator() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const isNew = quoteId === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [pricingRules, setPricingRules] = useState([]);
  const [generatingProposal, setGeneratingProposal] = useState(false);
  const [proposalHtml, setProposalHtml] = useState(null);
  
  // Dynamic tiers from pricing rules
  const [complexityTiers, setComplexityTiers] = useState(COMPLEXITY_TIERS);
  const [siteEnvironments, setSiteEnvironments] = useState(SITE_ENVIRONMENTS);
  
  // Form state
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [salesperson, setSalesperson] = useState("");
  const [projectSize, setProjectSize] = useState("medium");
  const [notes, setNotes] = useState("");
  
  // VEXOS Platform inputs
  const [residentialBuildings, setResidentialBuildings] = useState(1);
  const [amenityBuildings, setAmenityBuildings] = useState(0);
  const [floorsCovered, setFloorsCovered] = useState(10);
  const [coverageNotes, setCoverageNotes] = useState("");
  const [residentialUnits, setResidentialUnits] = useState(100);
  const [uniqueFloorplates, setUniqueFloorplates] = useState(5);
  const [repeatFloorplates, setRepeatFloorplates] = useState(5);
  const [licenseTerm, setLicenseTerm] = useState(12);
  
  // Exterior Modeling inputs
  const [exteriorStructures, setExteriorStructures] = useState([createEmptyStructure()]);
  const [siteEnvironment, setSiteEnvironment] = useState("none");
  const [includeRealityCapture, setIncludeRealityCapture] = useState(false);
  const [realityCaptureRate, setRealityCaptureRate] = useState(8500);
  
  // Orbit Configuration inputs
  const [includeMasterOrbit, setIncludeMasterOrbit] = useState(false);
  const [masterOrbitPrice, setMasterOrbitPrice] = useState(6000); // Default price, loaded from pricing rules
  const [includeGeneralAreaOrbit, setIncludeGeneralAreaOrbit] = useState(false);
  const [generalAreaOrbitPrice, setGeneralAreaOrbitPrice] = useState(7000); // Default price, loaded from pricing rules
  const [additionalOrbits, setAdditionalOrbits] = useState([]);
  const [additionalOrbitPrice, setAdditionalOrbitPrice] = useState(4000); // Default $4,000 per orbit
  
  // Day & Night Experience
  const [dayNightEnabled, setDayNightEnabled] = useState(false);
  const DAY_NIGHT_PRICE = 2500;
  
  // Additional Orbit Types
  const ADDITIONAL_ORBIT_TYPES = [
    { value: "ph_spotlight", label: "PH Spotlight Orbit", description: "Highlight penthouse or premium units" },
    { value: "amenity_upper", label: "Amenity / Upper Level Orbit", description: "Showcase amenity floors or upper luxury levels" },
    { value: "custom", label: "Custom Orbit", description: "Custom orbit experience" }
  ];
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    platform: true,
    exteriorModeling: true,
    orbitConfiguration: true,
    floorplates: true,
    recurring: true
  });

  // Load pricing rules and quote data
  useEffect(() => {
    async function loadData() {
      try {
        const rulesRes = await apiClient.get("/calculators/pricing-rules?calculator_type=vexos");
        setPricingRules(rulesRes.data);
        
        // Load dynamic complexity tiers from pricing rules
        const complexityFromRules = rulesRes.data
          .filter(r => r.category === "complexity" && r.active)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map(r => ({
            value: r.tier_config?.tier_value || r.label.toLowerCase().split(' ')[0],
            label: r.label.split(' (')[0],
            description: r.description,
            rate: r.default_rate
          }));
        if (complexityFromRules.length > 0) {
          setComplexityTiers(complexityFromRules);
        }
        
        // Load dynamic site environments from pricing rules
        const siteEnvFromRules = rulesRes.data
          .filter(r => r.category === "site_environment" && r.active)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map(r => ({
            value: r.tier_config?.tier_value || r.label.toLowerCase(),
            label: r.label,
            description: r.description,
            rate: r.default_rate
          }));
        if (siteEnvFromRules.length > 0) {
          setSiteEnvironments(siteEnvFromRules);
        }
        
        // Set reality capture rate from rules
        const rcRule = rulesRes.data.find(r => r.label.toLowerCase().includes("reality capture"));
        if (rcRule) setRealityCaptureRate(rcRule.default_rate);
        
        // Load Suborbit price from rules (or use default). The rule's
        // label was renamed from "Master Orbit" to "Suborbit"; match both
        // so a DB that hasn't been re-seeded still resolves.
        const masterOrbitRule = rulesRes.data.find(r => 
          r.category === "master_orbit" ||
          r.label.toLowerCase().includes("suborbit") ||
          r.label.toLowerCase().includes("master orbit")
        );
        if (masterOrbitRule) setMasterOrbitPrice(masterOrbitRule.default_rate);
        
        // Load General Area Orbit price from rules (or use default)
        const generalAreaOrbitRule = rulesRes.data.find(r => 
          r.label.toLowerCase().includes("general area orbit") || 
          r.category === "general_area_orbit"
        );
        if (generalAreaOrbitRule) setGeneralAreaOrbitPrice(generalAreaOrbitRule.default_rate);
        
        // Load Additional Orbit price from rules (or use default)
        const additionalOrbitRule = rulesRes.data.find(r => 
          r.label.toLowerCase().includes("additional orbit") || 
          r.category === "additional_orbit"
        );
        if (additionalOrbitRule) setAdditionalOrbitPrice(additionalOrbitRule.default_rate);
        
        if (!isNew) {
          const quoteRes = await apiClient.get(`/calculators/quotes/${quoteId}`);
          const quote = quoteRes.data;
          
          setProjectName(quote.project_name || "");
          setClientName(quote.client_name || "");
          setSalesperson(quote.salesperson || "");
          setNotes(quote.notes || "");
          
          quote.sections?.forEach(section => {
            const sectionType = section.section_type || "";
            
            // Handle exterior modeling section
            if (sectionType === "vexos_exterior_modeling") {
              const meta = section.metadata || {};
              setSiteEnvironment(meta.site_environment || "none");
              setIncludeRealityCapture(meta.reality_capture_enabled || false);
              
              // Restore structures from line items
              const structures = [];
              section.line_items?.forEach(item => {
                const itemMeta = item.metadata || {};
                if (itemMeta.item_type === "structure") {
                  structures.push({
                    id: item.id || crypto.randomUUID(),
                    name: itemMeta.structure_name || "",
                    complexity: itemMeta.complexity_tier || "medium",
                    uniqueCount: itemMeta.unique_count || 0,
                    repeatCount: itemMeta.repeat_count || 0,
                    notes: itemMeta.notes || ""
                  });
                }
              });
              if (structures.length > 0) {
                setExteriorStructures(structures);
              }
            }
            
            // Handle orbit configuration section
            if (sectionType === "vexos_orbit_configuration") {
              const meta = section.metadata || {};
              setIncludeMasterOrbit(meta.master_orbit_enabled || false);
              setIncludeGeneralAreaOrbit(meta.general_area_orbit_enabled || false);
              if (meta.day_night_enabled) setDayNightEnabled(true);
              
              // Restore additional orbits from line items
              const orbits = [];
              section.line_items?.forEach(item => {
                const itemMeta = item.metadata || {};
                if (itemMeta.item_type === "additional_orbit") {
                  orbits.push({
                    id: item.id || crypto.randomUUID(),
                    type: itemMeta.orbit_type || "ph_spotlight",
                    customName: itemMeta.custom_name || "",
                    quantity: item.quantity || 1
                  });
                }
              });
              if (orbits.length > 0) {
                setAdditionalOrbits(orbits);
              }
            }
            
            // Handle other sections
            section.line_items?.forEach(item => {
              const meta = item.metadata || {};
              if (item.label.includes("Residential Building")) {
                setResidentialBuildings(item.quantity || 1);
              } else if (item.label.includes("Amenity Building")) {
                setAmenityBuildings(item.quantity || 0);
              } else if (item.label.includes("Floors Covered") || item.label.includes("Floor Development")) {
                setFloorsCovered(meta.floors_covered || item.quantity || 10);
                setCoverageNotes(meta.coverage_notes || "");
              } else if (item.label.includes("Residential Unit") || item.label.includes("Inventory")) {
                setResidentialUnits(item.quantity || 100);
              } else if (item.label.includes("Unique")) {
                setUniqueFloorplates(item.quantity || 5);
              } else if (item.label.includes("Repeat")) {
                setRepeatFloorplates(item.quantity || 5);
              } else if (item.label.includes("License")) {
                setLicenseTerm(meta.term_months || 12);
                if (item.rate === 1500) setProjectSize("small");
                else if (item.rate === 3000) setProjectSize("medium");
                else setProjectSize("large");
              }
            });
          });
        }
      } catch (err) {
        toast.error("Failed to load data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [quoteId, isNew]);

  // Get rate from pricing rules
  function getRate(label, defaultVal = 0) {
    const rule = pricingRules.find(r => r.label.toLowerCase().includes(label.toLowerCase()));
    return rule?.default_rate || defaultVal;
  }

  // Get structure tier rate - uses complexity category from pricing rules
  function getStructureRate(complexity) {
    // First try to find from the new complexity category pricing rules
    const complexityRules = pricingRules.filter(r => r.category === "complexity");
    if (complexityRules.length > 0) {
      const rule = complexityRules.find(r => 
        r.tier_config?.tier_value === complexity || 
        r.label.toLowerCase().includes(complexity.toLowerCase())
      );
      if (rule) return rule.default_rate;
    }
    
    // Fallback to old label-based lookup
    const rateMap = {
      small: getRate("structure - small", DEFAULT_EXT_RATES.small),
      medium: getRate("structure - medium", DEFAULT_EXT_RATES.medium),
      large: getRate("structure - large", DEFAULT_EXT_RATES.large),
      xl: getRate("structure - xl", DEFAULT_EXT_RATES.xl)
    };
    return rateMap[complexity] || DEFAULT_EXT_RATES.medium;
  }

  // Get site environment rate - uses site_environment category from pricing rules
  function getSiteRate(env) {
    if (env === "none") return 0;
    
    // First try to find from the new site_environment category pricing rules
    const siteRules = pricingRules.filter(r => r.category === "site_environment");
    if (siteRules.length > 0) {
      const rule = siteRules.find(r => 
        r.tier_config?.tier_value === env || 
        r.label.toLowerCase() === env.toLowerCase()
      );
      if (rule) return rule.default_rate;
    }
    
    // Fallback to old label-based lookup
    const rateMap = {
      simple: getRate("site environment - simple", DEFAULT_EXT_RATES.site_simple),
      standard: getRate("site environment - standard", DEFAULT_EXT_RATES.site_standard),
      complex: getRate("site environment - complex", DEFAULT_EXT_RATES.site_complex),
      masterplan: getRate("site environment - masterplan", DEFAULT_EXT_RATES.site_masterplan)
    };
    return rateMap[env] || 0;
  }

  // Get replication rate
  function getReplicationRate() {
    return getRate("replication rate", DEFAULT_EXT_RATES.replication);
  }

  // Coverage calculation
  const coverage = useMemo(() => {
    return calculateCoverage(floorsCovered, uniqueFloorplates, repeatFloorplates);
  }, [floorsCovered, uniqueFloorplates, repeatFloorplates]);

  // Calculate exterior modeling totals
  const exteriorCalc = useMemo(() => {
    const replicationRate = getReplicationRate();
    
    // Calculate per-structure costs
    const structureDetails = exteriorStructures.map(s => {
      const tierRate = getStructureRate(s.complexity);
      const primaryCost = s.uniqueCount * tierRate;
      const replicationCost = s.repeatCount * replicationRate;
      const totalCost = primaryCost + replicationCost;
      
      return {
        ...s,
        tierRate,
        replicationRate,
        primaryCost,
        replicationCost,
        totalCost
      };
    });

    // Aggregate totals
    const totalUniqueStructures = structureDetails.reduce((sum, s) => sum + s.uniqueCount, 0);
    const totalReplicatedPlacements = structureDetails.reduce((sum, s) => sum + s.repeatCount, 0);
    const primaryStructureSubtotal = structureDetails.reduce((sum, s) => sum + s.primaryCost, 0);
    const replicationSubtotal = structureDetails.reduce((sum, s) => sum + s.replicationCost, 0);
    const siteEnvironmentCost = getSiteRate(siteEnvironment);
    const realityCaptureCost = includeRealityCapture ? realityCaptureRate : 0;
    
    const exteriorModelingTotal = primaryStructureSubtotal + replicationSubtotal + siteEnvironmentCost + realityCaptureCost;

    return {
      structureDetails,
      totalUniqueStructures,
      totalReplicatedPlacements,
      primaryStructureSubtotal,
      replicationSubtotal,
      siteEnvironmentCost,
      realityCaptureCost,
      exteriorModelingTotal
    };
  }, [exteriorStructures, siteEnvironment, includeRealityCapture, realityCaptureRate, pricingRules]);

  // Calculate orbit configuration totals
  const orbitCalc = useMemo(() => {
    const masterOrbitCost = includeMasterOrbit ? masterOrbitPrice : 0;
    const generalAreaOrbitCost = includeGeneralAreaOrbit ? generalAreaOrbitPrice : 0;
    
    // Calculate additional orbits costs
    const additionalOrbitDetails = additionalOrbits.map(orbit => {
      const orbitType = ADDITIONAL_ORBIT_TYPES.find(t => t.value === orbit.type);
      const subtotal = orbit.quantity * additionalOrbitPrice;
      return {
        ...orbit,
        typeLabel: orbitType?.label || "Custom Orbit",
        unitPrice: additionalOrbitPrice,
        subtotal
      };
    });
    
    const additionalOrbitsTotal = additionalOrbitDetails.reduce((sum, o) => sum + o.subtotal, 0);
    const totalAdditionalOrbitCount = additionalOrbits.reduce((sum, o) => sum + o.quantity, 0);
    const dayNightCost = dayNightEnabled ? DAY_NIGHT_PRICE : 0;
    const orbitConfigurationTotal = masterOrbitCost + generalAreaOrbitCost + additionalOrbitsTotal + dayNightCost;
    
    return {
      masterOrbitCost,
      generalAreaOrbitCost,
      additionalOrbitDetails,
      additionalOrbitsTotal,
      totalAdditionalOrbitCount,
      dayNightCost,
      orbitConfigurationTotal
    };
  }, [includeMasterOrbit, masterOrbitPrice, includeGeneralAreaOrbit, generalAreaOrbitPrice, additionalOrbits, additionalOrbitPrice, dayNightEnabled]);

  // Calculate platform & floorplate totals
  const calculations = useMemo(() => {
    const baseSetup = getRate("base platform");
    const perBuilding = getRate("additional residential");
    const perAmenity = getRate("amenity building");
    const perUnit = getRate("residential unit");
    const uniqueFloorplateRate = getRate("first unique");
    const repeatFloorplateRate = getRate("repeat");
    const monthlyLicense = PROJECT_SIZES.find(s => s.value === projectSize)?.license || 3000;

    const additionalBuildingsCost = Math.max(0, residentialBuildings - 1) * perBuilding;
    const amenityCost = amenityBuildings * perAmenity;
    const unitsCost = residentialUnits * perUnit;
    const platformSubtotal = baseSetup + additionalBuildingsCost + amenityCost + unitsCost;

    const uniqueFloorplateCost = uniqueFloorplates * uniqueFloorplateRate;
    const repeatFloorplateCost = repeatFloorplates * repeatFloorplateRate;
    const floorplateSubtotal = uniqueFloorplateCost + repeatFloorplateCost;

    const totalRecurring = monthlyLicense * licenseTerm;
    
    // One-time total now includes exterior modeling and orbit configuration
    const oneTimeTotal = platformSubtotal + exteriorCalc.exteriorModelingTotal + orbitCalc.orbitConfigurationTotal + floorplateSubtotal;

    return {
      baseSetup,
      additionalBuildingsCost,
      amenityCost,
      unitsCost,
      platformSubtotal,
      uniqueFloorplateRate,
      repeatFloorplateRate,
      uniqueFloorplateCost,
      repeatFloorplateCost,
      floorplateSubtotal,
      monthlyLicense,
      totalRecurring,
      oneTimeTotal,
      grandTotal: oneTimeTotal + totalRecurring
    };
  }, [pricingRules, residentialBuildings, amenityBuildings, residentialUnits, 
      uniqueFloorplates, repeatFloorplates, projectSize, licenseTerm, exteriorCalc.exteriorModelingTotal, orbitCalc.orbitConfigurationTotal]);

  // Build deliverables for timeline estimation
  const timelineDeliverables = useMemo(() => {
    const items = [];
    // Exterior Modeling
    if (exteriorCalc.exteriorModelingTotal > 0) {
      items.push({ deliverable_type: "High Rise Model", quantity: exteriorCalc.totalUniqueStructures || residentialBuildings || 1 });
    }
    // VEXOS Platform
    if (calculations.platformSubtotal > 0) {
      items.push({ deliverable_type: "VEXOS Platform Setup", quantity: 1 });
    }
    // Orbit Configuration
    if (orbitCalc.orbitConfigurationTotal > 0) {
      items.push({ deliverable_type: "Interactive Experience", quantity: (includeMasterOrbit ? 1 : 0) + orbitCalc.totalAdditionalOrbitCount });
    }
    // Floorplates
    if (calculations.floorplateSubtotal > 0) {
      items.push({ deliverable_type: "Floor Plans", quantity: uniqueFloorplates + repeatFloorplates });
    }
    return items;
  }, [exteriorCalc, calculations, orbitCalc, residentialBuildings, includeMasterOrbit, uniqueFloorplates, repeatFloorplates]);

  // Structure management functions
  function addStructure() {
    setExteriorStructures([...exteriorStructures, createEmptyStructure()]);
  }

  function updateStructure(id, field, value) {
    setExteriorStructures(exteriorStructures.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  }

  function removeStructure(id) {
    if (exteriorStructures.length > 1) {
      setExteriorStructures(exteriorStructures.filter(s => s.id !== id));
    }
  }
  
  // Additional orbit management functions
  function addAdditionalOrbit() {
    setAdditionalOrbits([...additionalOrbits, {
      id: crypto.randomUUID(),
      type: "ph_spotlight",
      customName: "",
      quantity: 1
    }]);
  }
  
  function updateAdditionalOrbit(id, field, value) {
    setAdditionalOrbits(additionalOrbits.map(o => 
      o.id === id ? { ...o, [field]: value } : o
    ));
  }
  
  function removeAdditionalOrbit(id) {
    setAdditionalOrbits(additionalOrbits.filter(o => o.id !== id));
  }

  // Build quote sections for saving
  function buildSections() {
    const monthlyLicense = PROJECT_SIZES.find(s => s.value === projectSize)?.license || 3000;
    const replicationRate = getReplicationRate();
    
    return [
      {
        id: crypto.randomUUID(),
        section_type: "vexos_platform",
        label: "Platform Configuration",
        line_items: [
          {
            id: crypto.randomUUID(),
            label: "Base Platform Setup",
            quantity: 1,
            rate: getRate("base platform"),
            subtotal: getRate("base platform")
          },
          {
            id: crypto.randomUUID(),
            label: "Additional Residential Buildings",
            quantity: Math.max(0, residentialBuildings - 1),
            rate: getRate("additional residential"),
            subtotal: calculations.additionalBuildingsCost,
            unit_type: "building"
          },
          {
            id: crypto.randomUUID(),
            label: "Amenity Buildings",
            quantity: amenityBuildings,
            rate: getRate("amenity building"),
            subtotal: calculations.amenityCost,
            unit_type: "building"
          },
          {
            id: crypto.randomUUID(),
            label: "Floors Covered",
            quantity: 0,
            rate: 0,
            subtotal: 0,
            unit_type: "floor",
            metadata: { 
              floors_covered: floorsCovered,
              coverage_notes: coverageNotes,
              planning_only: true 
            }
          },
          {
            id: crypto.randomUUID(),
            label: "Residential Inventory",
            quantity: residentialUnits,
            rate: getRate("residential unit"),
            subtotal: calculations.unitsCost,
            unit_type: "unit"
          }
        ],
        subtotal: calculations.platformSubtotal
      },
      // Exterior Modeling Section
      {
        id: crypto.randomUUID(),
        section_type: "vexos_exterior_modeling",
        label: "Exterior Modeling",
        line_items: [
          // Structure line items
          ...exteriorCalc.structureDetails.filter(s => s.uniqueCount > 0 || s.repeatCount > 0).map(s => ({
            id: s.id,
            label: s.name || `${complexityTiers.find(t => t.value === s.complexity)?.label || 'Medium'} Structure`,
            quantity: s.uniqueCount,
            rate: s.tierRate,
            subtotal: s.totalCost,
            unit_type: "structure",
            metadata: {
              item_type: "structure",
              structure_name: s.name,
              complexity_tier: s.complexity,
              tier_rate: s.tierRate,
              unique_count: s.uniqueCount,
              repeat_count: s.repeatCount,
              replication_rate: replicationRate,
              primary_cost: s.primaryCost,
              replication_cost: s.replicationCost,
              notes: s.notes
            }
          })),
          // Site environment line item
          ...(exteriorCalc.siteEnvironmentCost > 0 ? [{
            id: crypto.randomUUID(),
            label: `Site Environment — ${siteEnvironments.find(e => e.value === siteEnvironment)?.label || 'Standard'}`,
            quantity: 1,
            rate: exteriorCalc.siteEnvironmentCost,
            subtotal: exteriorCalc.siteEnvironmentCost,
            unit_type: "site",
            metadata: {
              item_type: "site_environment",
              environment_type: siteEnvironment
            }
          }] : []),
          // Reality capture line item
          ...(exteriorCalc.realityCaptureCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "Reality Capture & Environment Reconstruction",
            description: "Drone capture, photogrammetry, terrain reconstruction",
            quantity: 1,
            rate: realityCaptureRate,
            subtotal: realityCaptureRate,
            metadata: {
              item_type: "reality_capture"
            }
          }] : [])
        ],
        subtotal: exteriorCalc.exteriorModelingTotal,
        metadata: {
          total_unique_structures: exteriorCalc.totalUniqueStructures,
          total_replicated_placements: exteriorCalc.totalReplicatedPlacements,
          primary_structure_subtotal: exteriorCalc.primaryStructureSubtotal,
          replication_subtotal: exteriorCalc.replicationSubtotal,
          site_environment: siteEnvironment,
          site_environment_cost: exteriorCalc.siteEnvironmentCost,
          reality_capture_enabled: includeRealityCapture,
          reality_capture_rate: realityCaptureRate
        }
      },
      // Orbit Configuration Section (only include if there are orbits configured)
      ...(orbitCalc.orbitConfigurationTotal > 0 ? [{
        id: crypto.randomUUID(),
        section_type: "vexos_orbit_configuration",
        label: "Orbit Configuration",
        line_items: [
          // Suborbit line item (internal keys keep the retired name)
          ...(orbitCalc.masterOrbitCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "Suborbit",
            description: "Contextual overview orbit showcasing the project within its surroundings",
            quantity: 1,
            rate: masterOrbitPrice,
            subtotal: orbitCalc.masterOrbitCost,
            unit_type: "orbit",
            metadata: {
              item_type: "master_orbit",
              orbit_price: masterOrbitPrice
            }
          }] : []),
          // General Area Orbit line item
          ...(orbitCalc.generalAreaOrbitCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "General Area Orbit",
            description: "Neighborhood and area context orbit showcasing local surroundings",
            quantity: 1,
            rate: generalAreaOrbitPrice,
            subtotal: orbitCalc.generalAreaOrbitCost,
            unit_type: "orbit",
            metadata: {
              item_type: "general_area_orbit",
              orbit_price: generalAreaOrbitPrice
            }
          }] : []),
          // Additional orbit line items
          ...orbitCalc.additionalOrbitDetails.map(orbit => ({
            id: orbit.id,
            label: orbit.type === "custom" 
              ? `Additional Orbit – Custom: '${orbit.customName || 'Unnamed'}'`
              : `Additional Orbit – ${orbit.typeLabel}`,
            description: ADDITIONAL_ORBIT_TYPES.find(t => t.value === orbit.type)?.description || "",
            quantity: orbit.quantity,
            rate: orbit.unitPrice,
            subtotal: orbit.subtotal,
            unit_type: "orbit",
            metadata: {
              item_type: "additional_orbit",
              orbit_type: orbit.type,
              custom_name: orbit.customName,
              unit_price: orbit.unitPrice
            }
          })),
          // Day & Night Experience line item
          ...(orbitCalc.dayNightCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "Day & Night Experience",
            description: "The experience includes both daytime and nighttime environments, allowing for enhanced visual storytelling, lighting conditions, and emotional impact during presentations and marketing use.",
            quantity: 1,
            rate: DAY_NIGHT_PRICE,
            subtotal: DAY_NIGHT_PRICE,
            unit_type: "enhancement",
            metadata: {
              item_type: "day_night_experience"
            }
          }] : [])
        ],
        subtotal: orbitCalc.orbitConfigurationTotal,
        metadata: {
          master_orbit_enabled: includeMasterOrbit,
          master_orbit_price: masterOrbitPrice,
          general_area_orbit_enabled: includeGeneralAreaOrbit,
          general_area_orbit_price: generalAreaOrbitPrice,
          additional_orbit_price: additionalOrbitPrice,
          total_additional_orbit_count: orbitCalc.totalAdditionalOrbitCount,
          day_night_enabled: dayNightEnabled,
          day_night_price: DAY_NIGHT_PRICE
        }
      }] : []),
      {
        id: crypto.randomUUID(),
        section_type: "vexos_floorplates",
        label: "Floorplate Development",
        line_items: [
          {
            id: crypto.randomUUID(),
            label: "Unique Floorplates",
            quantity: uniqueFloorplates,
            rate: calculations.uniqueFloorplateRate,
            subtotal: calculations.uniqueFloorplateCost,
            unit_type: "floorplate"
          },
          {
            id: crypto.randomUUID(),
            label: "Repeat Floorplates",
            quantity: repeatFloorplates,
            rate: calculations.repeatFloorplateRate,
            subtotal: calculations.repeatFloorplateCost,
            unit_type: "floorplate"
          }
        ],
        subtotal: calculations.floorplateSubtotal,
        metadata: {
          floors_covered: floorsCovered,
          total_floorplates: uniqueFloorplates + repeatFloorplates,
          coverage_status: coverage.status
        }
      },
      {
        id: crypto.randomUUID(),
        section_type: "vexos_recurring",
        label: "Platform License",
        line_items: [
          {
            id: crypto.randomUUID(),
            label: `Monthly License — ${PROJECT_SIZES.find(s => s.value === projectSize)?.label}`,
            quantity: licenseTerm,
            rate: monthlyLicense,
            subtotal: calculations.totalRecurring,
            unit_type: "month",
            metadata: { term_months: licenseTerm, project_size: projectSize }
          }
        ],
        subtotal: calculations.totalRecurring
      }
    ];
  }

  async function handleSave() {
    if (!projectName.trim()) {
      toast.error("Project name required");
      return;
    }
    if (!clientName.trim()) {
      toast.error("Client name required");
      return;
    }
    if (!salesperson) {
      toast.error("Select a salesperson");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        project_name: projectName,
        client_name: clientName,
        salesperson,
        quote_type: "vexos",
        sections: buildSections(),
        payment_plan: {
          structure: "milestone",
          upfront_percent: 50,
          recurring_monthly: calculations.monthlyLicense,
          recurring_term_months: licenseTerm
        },
        notes
      };

      if (isNew) {
        const res = await apiClient.post("/calculators/quotes", payload);
        toast.success("Quote created");
        navigate(`/sales/calculators/vexos/${res.data.id}`, { replace: true });
      } else {
        await apiClient.put(`/calculators/quotes/${quoteId}`, payload);
        toast.success("Quote saved");
      }
    } catch (err) {
      toast.error("Failed to save");
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  function toggleSection(section) {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }

  async function handleGenerateProposal() {
    if (!quoteId || isNew) {
      toast.error("Please save the quote first");
      return;
    }

    setGeneratingProposal(true);
    try {
      const res = await apiClient.post(`/calculators/quotes/${quoteId}/generate-proposal`, {
        format: "html"
      });
      setProposalHtml(res.data.html);
      toast.success("Proposal generated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate proposal");
    } finally {
      setGeneratingProposal(false);
    }
  }

  function handlePrintProposal() {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(proposalHtml);
    printWindow.document.close();
    printWindow.print();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/sales/calculators")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                <h1 className="text-xl font-light">VEXOS Quote</h1>
              </div>
              <p className="text-sm text-muted-foreground">
                {isNew ? "New Quote" : projectName || "Untitled"}
              </p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Panel - Inputs */}
          <div className="col-span-2 space-y-6">
            {/* Project Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Project</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Project Name</Label>
                    <Input
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="e.g., One River Point"
                      data-testid="project-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <Input
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="e.g., Related Group"
                      data-testid="client-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Salesperson</Label>
                    <Select value={salesperson} onValueChange={setSalesperson}>
                      <SelectTrigger data-testid="salesperson-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {SALESPEOPLE.map(name => (
                          <SelectItem key={name} value={name}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Project Scale</Label>
                    <Select value={projectSize} onValueChange={setProjectSize}>
                      <SelectTrigger data-testid="project-size-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_SIZES.map(size => (
                          <SelectItem key={size.value} value={size.value}>
                            {size.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Determines monthly license rate
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Platform Configuration */}
            <Collapsible open={expandedSections.platform} onOpenChange={() => toggleSection("platform")}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Layers className="w-5 h-5 text-blue-500" />
                        Platform Configuration
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-blue-500">
                          ${calculations.platformSubtotal.toLocaleString()}
                        </span>
                        {expandedSections.platform ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Residential Buildings</Label>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" onClick={() => setResidentialBuildings(Math.max(1, residentialBuildings - 1))}>
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            type="number"
                            value={residentialBuildings}
                            onChange={(e) => setResidentialBuildings(Math.max(1, parseInt(e.target.value) || 1))}
                            className="text-center"
                            data-testid="residential-buildings-input"
                          />
                          <Button variant="outline" size="icon" onClick={() => setResidentialBuildings(residentialBuildings + 1)}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          First included • +${getRate("additional residential").toLocaleString()} each additional
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Amenity Buildings</Label>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" onClick={() => setAmenityBuildings(Math.max(0, amenityBuildings - 1))}>
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            type="number"
                            value={amenityBuildings}
                            onChange={(e) => setAmenityBuildings(Math.max(0, parseInt(e.target.value) || 0))}
                            className="text-center"
                            data-testid="amenity-buildings-input"
                          />
                          <Button variant="outline" size="icon" onClick={() => setAmenityBuildings(amenityBuildings + 1)}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          ${getRate("amenity building").toLocaleString()} each
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border/50">
                      <div className="space-y-2">
                        <Label>Floors Covered</Label>
                        <Input
                          type="number"
                          value={floorsCovered}
                          onChange={(e) => setFloorsCovered(Math.max(1, parseInt(e.target.value) || 1))}
                          data-testid="floors-covered-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Enter only floors requiring VEXOS coverage. Exclude garage, podium, service levels.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Coverage Notes</Label>
                        <Textarea
                          value={coverageNotes}
                          onChange={(e) => setCoverageNotes(e.target.value)}
                          placeholder="e.g., Residences begin on level 13. Amenity on level 57."
                          rows={2}
                          className="text-sm resize-none"
                          data-testid="coverage-notes-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Residential Inventory</Label>
                      <Input
                        type="number"
                        value={residentialUnits}
                        onChange={(e) => setResidentialUnits(Math.max(1, parseInt(e.target.value) || 1))}
                        data-testid="residential-units-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        ${getRate("residential unit")} per unit
                      </p>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Exterior Modeling - NEW SECTION */}
            <Collapsible open={expandedSections.exteriorModeling} onOpenChange={() => toggleSection("exteriorModeling")}>
              <Card className="border-2 border-emerald-500/20">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Mountain className="w-5 h-5 text-emerald-500" />
                        Exterior Modeling
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-emerald-500">
                          ${exteriorCalc.exteriorModelingTotal.toLocaleString()}
                        </span>
                        {expandedSections.exteriorModeling ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    {/* Structure Rows */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <h4 className="font-semibold">Primary Structures</h4>
                        </div>
                        <Button variant="outline" size="sm" onClick={addStructure} className="gap-1">
                          <Plus className="w-3 h-3" /> Add Structure
                        </Button>
                      </div>

                      {exteriorStructures.map((structure, index) => {
                        const details = exteriorCalc.structureDetails.find(d => d.id === structure.id);
                        return (
                          <div 
                            key={structure.id} 
                            className={`p-4 rounded-xl border-2 transition-all ${
                              (structure.uniqueCount > 0 || structure.repeatCount > 0) 
                                ? 'border-emerald-500/30 bg-emerald-500/5' 
                                : 'border-border'
                            }`}
                            data-testid={`structure-row-${index}`}
                          >
                            <div className="grid grid-cols-12 gap-4 items-start">
                              {/* Structure Name */}
                              <div className="col-span-4 space-y-1">
                                <Label className="text-xs text-muted-foreground">Structure Name</Label>
                                <Input
                                  value={structure.name}
                                  onChange={(e) => updateStructure(structure.id, "name", e.target.value)}
                                  placeholder="e.g., Tower A, Villa Model B"
                                  className="text-sm"
                                />
                              </div>

                              {/* Complexity Tier */}
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs text-muted-foreground">Complexity</Label>
                                <Select 
                                  value={structure.complexity} 
                                  onValueChange={(v) => updateStructure(structure.id, "complexity", v)}
                                >
                                  <SelectTrigger className="text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {complexityTiers.map(tier => (
                                      <SelectItem key={tier.value} value={tier.value}>
                                        <div className="flex flex-col">
                                          <span>{tier.label}</span>
                                          <span className="text-xs text-muted-foreground">{tier.description}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                  ${getStructureRate(structure.complexity).toLocaleString()}/ea
                                </p>
                              </div>

                              {/* Unique Count */}
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs text-muted-foreground">Unique</Label>
                                <div className="flex items-center gap-1">
                                  <Button 
                                    variant="outline" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => updateStructure(structure.id, "uniqueCount", Math.max(0, structure.uniqueCount - 1))}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </Button>
                                  <Input
                                    type="number"
                                    value={structure.uniqueCount}
                                    onChange={(e) => updateStructure(structure.id, "uniqueCount", Math.max(0, parseInt(e.target.value) || 0))}
                                    className="text-center text-sm h-8"
                                  />
                                  <Button 
                                    variant="outline" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => updateStructure(structure.id, "uniqueCount", structure.uniqueCount + 1)}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>

                              {/* Repeat Count */}
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Repeat className="w-3 h-3" /> Repeated
                                </Label>
                                <div className="flex items-center gap-1">
                                  <Button 
                                    variant="outline" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => updateStructure(structure.id, "repeatCount", Math.max(0, structure.repeatCount - 1))}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </Button>
                                  <Input
                                    type="number"
                                    value={structure.repeatCount}
                                    onChange={(e) => updateStructure(structure.id, "repeatCount", Math.max(0, parseInt(e.target.value) || 0))}
                                    className="text-center text-sm h-8"
                                  />
                                  <Button 
                                    variant="outline" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => updateStructure(structure.id, "repeatCount", structure.repeatCount + 1)}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                                {structure.repeatCount > 0 && (
                                  <p className="text-xs text-muted-foreground">
                                    ${getReplicationRate()}/placement
                                  </p>
                                )}
                              </div>

                              {/* Row Total & Delete */}
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs text-muted-foreground">Row Total</Label>
                                <div className="flex items-center gap-2">
                                  <div className="h-8 flex-1 px-3 rounded-md bg-muted/50 flex items-center justify-end">
                                    <span className="font-semibold text-sm text-emerald-600">
                                      ${(details?.totalCost || 0).toLocaleString()}
                                    </span>
                                  </div>
                                  {exteriorStructures.length > 1 && (
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                      onClick={() => removeStructure(structure.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Cost Breakdown for row */}
                            {(structure.uniqueCount > 0 || structure.repeatCount > 0) && (
                              <div className="mt-3 pt-3 border-t flex items-center gap-4 text-xs text-muted-foreground">
                                {details?.primaryCost > 0 && (
                                  <span>Primary: ${details.primaryCost.toLocaleString()}</span>
                                )}
                                {details?.replicationCost > 0 && (
                                  <span>Replication: ${details.replicationCost.toLocaleString()}</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Site Environment */}
                    <div className="space-y-3 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <Trees className="w-4 h-4 text-muted-foreground" />
                        <h4 className="font-semibold">Site Environment</h4>
                      </div>
                      <Select value={siteEnvironment} onValueChange={setSiteEnvironment}>
                        <SelectTrigger data-testid="site-environment-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {siteEnvironments.map(env => (
                            <SelectItem key={env.value} value={env.value}>
                              <div className="flex items-center justify-between gap-4 w-full">
                                <div>
                                  <span className="font-medium">{env.label}</span>
                                  <p className="text-xs text-muted-foreground">{env.description}</p>
                                </div>
                                {env.value !== "none" && (
                                  <span className="text-xs font-medium">
                                    ${getSiteRate(env.value).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Reality Capture */}
                    <div className="space-y-3 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">Reality Capture & Environment Reconstruction</h4>
                          <p className="text-sm text-muted-foreground">Drone capture, photogrammetry, terrain reconstruction</p>
                        </div>
                        <div className="flex items-center gap-4">
                          {includeRealityCapture && (
                            <Input
                              type="number"
                              value={realityCaptureRate}
                              onChange={(e) => setRealityCaptureRate(parseInt(e.target.value) || 8500)}
                              className="w-28"
                              data-testid="reality-capture-rate-input"
                            />
                          )}
                          <Switch
                            checked={includeRealityCapture}
                            onCheckedChange={setIncludeRealityCapture}
                            data-testid="reality-capture-toggle"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Exterior Modeling Summary */}
                    <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20 space-y-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <Calculator className="w-4 h-4" />
                        Exterior Modeling Summary
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Unique Structures</p>
                          <p className="font-medium">{exteriorCalc.totalUniqueStructures}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Replicated</p>
                          <p className="font-medium">{exteriorCalc.totalReplicatedPlacements}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Site Complexity</p>
                          <p className="font-medium">{siteEnvironments.find(e => e.value === siteEnvironment)?.label || 'None'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Reality Capture</p>
                          <p className="font-medium">{includeRealityCapture ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                      <div className="pt-3 border-t flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Exterior Modeling Total</span>
                        <span className="text-lg font-bold text-emerald-600">${exteriorCalc.exteriorModelingTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Orbit Configuration - NEW SECTION */}
            <Collapsible open={expandedSections.orbitConfiguration} onOpenChange={() => toggleSection("orbitConfiguration")}>
              <Card className="border-2 border-purple-500/20">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Orbit className="w-5 h-5 text-purple-500" />
                        Orbit Configuration
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-purple-500">
                          ${orbitCalc.orbitConfigurationTotal.toLocaleString()}
                        </span>
                        {expandedSections.orbitConfiguration ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    {/* Included Orbit Info */}
                    <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/20">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm">Standard Building Orbit Included</p>
                          <p className="text-sm text-muted-foreground">
                            Each residential building includes one standard building orbit. This is the core orbit experience for viewing individual buildings.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Suborbit Add-On */}
                    <div className="space-y-3 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-muted-foreground" />
                            <h4 className="font-semibold">Suborbit</h4>
                            <Badge variant="outline" className="text-xs">Optional Add-On</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            A wider contextual orbit used to showcase the overall site, surroundings, and project location before entering the core building experience.
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          {includeMasterOrbit && (
                            <span className="text-sm font-medium text-purple-600">
                              ${masterOrbitPrice.toLocaleString()}
                            </span>
                          )}
                          <Switch
                            checked={includeMasterOrbit}
                            onCheckedChange={setIncludeMasterOrbit}
                            data-testid="master-orbit-toggle"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* General Area Orbit Add-On */}
                    <div className="space-y-3 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            <h4 className="font-semibold">General Area Orbit</h4>
                            <Badge variant="outline" className="text-xs">Optional Add-On</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            Neighborhood and area context orbit showcasing local surroundings and points of interest.
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          {includeGeneralAreaOrbit && (
                            <span className="text-sm font-medium text-purple-600">
                              ${generalAreaOrbitPrice.toLocaleString()}
                            </span>
                          )}
                          <Switch
                            checked={includeGeneralAreaOrbit}
                            onCheckedChange={setIncludeGeneralAreaOrbit}
                            data-testid="general-area-orbit-toggle"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Additional Orbits */}
                    <div className="space-y-4 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-muted-foreground" />
                            <h4 className="font-semibold">Additional Orbits</h4>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Add extra orbit experiences within an already-counted building, such as a PH spotlight, upper-level luxury stack, amenity segment, or custom guided orbit.
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={addAdditionalOrbit} className="gap-1">
                          <Plus className="w-3 h-3" /> Add Orbit
                        </Button>
                      </div>

                      {additionalOrbits.length === 0 ? (
                        <div className="text-center py-6 text-muted-foreground text-sm border rounded-lg border-dashed">
                          No additional orbits added. Click "Add Orbit" to add specialty orbit experiences.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {additionalOrbits.map((orbit, index) => {
                            const orbitDetails = orbitCalc.additionalOrbitDetails.find(o => o.id === orbit.id);
                            return (
                              <div 
                                key={orbit.id}
                                className="p-4 rounded-xl border-2 border-purple-500/20 bg-purple-500/5"
                                data-testid={`additional-orbit-row-${index}`}
                              >
                                <div className="grid grid-cols-12 gap-4 items-start">
                                  {/* Orbit Type */}
                                  <div className="col-span-4 space-y-1">
                                    <Label className="text-xs text-muted-foreground">Orbit Type</Label>
                                    <Select 
                                      value={orbit.type} 
                                      onValueChange={(v) => updateAdditionalOrbit(orbit.id, "type", v)}
                                    >
                                      <SelectTrigger className="text-sm">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {ADDITIONAL_ORBIT_TYPES.map(type => (
                                          <SelectItem key={type.value} value={type.value}>
                                            <div className="flex flex-col">
                                              <span>{type.label}</span>
                                              <span className="text-xs text-muted-foreground">{type.description}</span>
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  {/* Custom Name (conditional) */}
                                  <div className={`col-span-3 space-y-1 ${orbit.type !== "custom" ? "opacity-50" : ""}`}>
                                    <Label className="text-xs text-muted-foreground">
                                      {orbit.type === "custom" ? "Custom Orbit Name *" : "Custom Name"}
                                    </Label>
                                    <Input
                                      value={orbit.customName}
                                      onChange={(e) => updateAdditionalOrbit(orbit.id, "customName", e.target.value)}
                                      placeholder={orbit.type === "custom" ? "e.g., Sky Lounge Entry" : "Optional"}
                                      className="text-sm"
                                      disabled={orbit.type !== "custom"}
                                    />
                                  </div>

                                  {/* Quantity */}
                                  <div className="col-span-2 space-y-1">
                                    <Label className="text-xs text-muted-foreground">Quantity</Label>
                                    <div className="flex items-center gap-1">
                                      <Button 
                                        variant="outline" 
                                        size="icon" 
                                        className="h-8 w-8"
                                        onClick={() => updateAdditionalOrbit(orbit.id, "quantity", Math.max(1, orbit.quantity - 1))}
                                      >
                                        <Minus className="w-3 h-3" />
                                      </Button>
                                      <Input
                                        type="number"
                                        value={orbit.quantity}
                                        onChange={(e) => updateAdditionalOrbit(orbit.id, "quantity", Math.max(1, parseInt(e.target.value) || 1))}
                                        className="text-center text-sm h-8"
                                        min={1}
                                      />
                                      <Button 
                                        variant="outline" 
                                        size="icon" 
                                        className="h-8 w-8"
                                        onClick={() => updateAdditionalOrbit(orbit.id, "quantity", orbit.quantity + 1)}
                                      >
                                        <Plus className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Unit Price (read-only) */}
                                  <div className="col-span-1 space-y-1">
                                    <Label className="text-xs text-muted-foreground">Unit Price</Label>
                                    <div className="h-8 px-2 rounded-md bg-muted/50 flex items-center justify-center">
                                      <span className="text-sm">${additionalOrbitPrice.toLocaleString()}</span>
                                    </div>
                                  </div>

                                  {/* Row Total */}
                                  <div className="col-span-2 space-y-1">
                                    <Label className="text-xs text-muted-foreground">Total</Label>
                                    <div className="flex items-center gap-2">
                                      <div className="h-8 flex-1 px-3 rounded-md bg-muted/50 flex items-center justify-end">
                                        <span className="font-semibold text-sm text-purple-600">
                                          ${(orbitDetails?.subtotal || 0).toLocaleString()}
                                        </span>
                                      </div>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                        onClick={() => removeAdditionalOrbit(orbit.id)}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>


                    {/* Day & Night Experience — Enhancement */}
                    <div className="space-y-3 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Sun className="w-4 h-4 text-amber-400" />
                            <Moon className="w-4 h-4 text-indigo-400 -ml-1" />
                            <h4 className="font-semibold">Day & Night Experience</h4>
                            <Badge className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/20 hover:bg-amber-500/10">Recommended</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            Dual-environment experience including both daytime and nighttime lighting, atmosphere, and presentation modes.
                          </p>
                          <p className="text-xs text-muted-foreground/60 mt-0.5 italic">
                            Enhances realism, mood, and overall presentation impact.
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          {dayNightEnabled && (
                            <span className="text-sm font-medium text-amber-600">
                              ${DAY_NIGHT_PRICE.toLocaleString()}
                            </span>
                          )}
                          <Switch
                            checked={dayNightEnabled}
                            onCheckedChange={setDayNightEnabled}
                            data-testid="day-night-toggle"
                          />
                        </div>
                      </div>
                      {dayNightEnabled && (
                        <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/10 mt-2">
                          <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs font-medium text-amber-700">Sales Positioning</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                "We can also include a full day-to-night experience — this significantly improves how the project is perceived, especially for presentations and marketing."
                              </p>
                              <p className="text-[10px] text-muted-foreground/60 mt-1 italic">
                                Day shows the architecture — night sells the lifestyle.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Orbit Configuration Summary */}
                    {orbitCalc.orbitConfigurationTotal > 0 && (
                      <div className="p-4 rounded-xl bg-purple-500/5 border border-purple-500/20 space-y-3">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <Calculator className="w-4 h-4" />
                          Orbit Configuration Summary
                        </h4>
                        <div className="space-y-2 text-sm">
                          {orbitCalc.masterOrbitCost > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Suborbit</span>
                              <span className="font-medium">${orbitCalc.masterOrbitCost.toLocaleString()}</span>
                            </div>
                          )}
                          {orbitCalc.additionalOrbitDetails.map(orbit => (
                            <div key={orbit.id} className="flex items-center justify-between">
                              <span className="text-muted-foreground">
                                {orbit.type === "custom" 
                                  ? `Custom: ${orbit.customName || 'Unnamed'} (x${orbit.quantity})`
                                  : `${orbit.typeLabel} (x${orbit.quantity})`
                                }
                              </span>
                              <span className="font-medium">${orbit.subtotal.toLocaleString()}</span>
                            </div>
                          ))}
                          {orbitCalc.dayNightCost > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Day & Night Experience</span>
                              <span className="font-medium">${orbitCalc.dayNightCost.toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                        <div className="pt-3 border-t flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Orbit Configuration Total</span>
                          <span className="text-lg font-bold text-purple-600">${orbitCalc.orbitConfigurationTotal.toLocaleString()}</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Floorplate Development */}
            <Collapsible open={expandedSections.floorplates} onOpenChange={() => toggleSection("floorplates")}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Home className="w-5 h-5 text-purple-500" />
                        Floorplate Development
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-purple-500">
                          ${calculations.floorplateSubtotal.toLocaleString()}
                        </span>
                        {expandedSections.floorplates ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Unique Floorplates</Label>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" onClick={() => setUniqueFloorplates(Math.max(0, uniqueFloorplates - 1))}>
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            type="number"
                            value={uniqueFloorplates}
                            onChange={(e) => setUniqueFloorplates(Math.max(0, parseInt(e.target.value) || 0))}
                            className="text-center"
                            data-testid="unique-floorplates-input"
                          />
                          <Button variant="outline" size="icon" onClick={() => setUniqueFloorplates(uniqueFloorplates + 1)}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          ${calculations.uniqueFloorplateRate.toLocaleString()} each
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Repeat Floorplates</Label>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" onClick={() => setRepeatFloorplates(Math.max(0, repeatFloorplates - 1))}>
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            type="number"
                            value={repeatFloorplates}
                            onChange={(e) => setRepeatFloorplates(Math.max(0, parseInt(e.target.value) || 0))}
                            className="text-center"
                            data-testid="repeat-floorplates-input"
                          />
                          <Button variant="outline" size="icon" onClick={() => setRepeatFloorplates(repeatFloorplates + 1)}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          ${calculations.repeatFloorplateRate.toLocaleString()} each
                        </p>
                      </div>
                    </div>

                    <div 
                      className={`p-4 rounded-lg border transition-colors ${
                        coverage.statusType === "success" 
                          ? "bg-emerald-500/5 border-emerald-500/20" 
                          : "bg-amber-500/5 border-amber-500/20"
                      }`}
                      data-testid="coverage-summary"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-3 flex-1">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            {coverage.statusType === "success" ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-amber-500" />
                            )}
                            <span>Coverage Summary</span>
                          </div>
                          
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Floors to Cover</p>
                              <p className="font-medium">{floorsCovered}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Unique</p>
                              <p className="font-medium">{uniqueFloorplates}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Repeat</p>
                              <p className="font-medium">{repeatFloorplates}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Total Covered</p>
                              <p className="font-medium">{coverage.totalCovered}</p>
                            </div>
                          </div>
                          
                          <p className={`text-sm font-medium ${
                            coverage.statusType === "success" ? "text-emerald-600" : "text-amber-600"
                          }`}>
                            {coverage.status}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Platform License */}
            <Collapsible open={expandedSections.recurring} onOpenChange={() => toggleSection("recurring")}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-amber-500" />
                        Platform License
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-amber-500">
                          ${calculations.totalRecurring.toLocaleString()}
                        </span>
                        {expandedSections.recurring ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Term</Label>
                        <Select value={licenseTerm.toString()} onValueChange={(v) => setLicenseTerm(parseInt(v))}>
                          <SelectTrigger data-testid="license-term-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="6">6 months</SelectItem>
                            <SelectItem value="12">12 months</SelectItem>
                            <SelectItem value="18">18 months</SelectItem>
                            <SelectItem value="24">24 months</SelectItem>
                            <SelectItem value="36">36 months</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Monthly Rate</Label>
                        <div className="h-10 px-3 py-2 rounded-md border bg-muted/50 flex items-center">
                          <span className="font-medium">${calculations.monthlyLicense.toLocaleString()}/mo</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Based on project scale</p>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Internal Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes visible only to your team..."
                  rows={3}
                  data-testid="internal-notes-input"
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Premium Quote Summary */}
          <div className="space-y-4">
            <Card className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto border-white/[0.08] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] bg-[#000000] rounded-[24px]" style={{ scrollbarColor: 'rgba(255,255,255,0.12) transparent', scrollbarWidth: 'thin' }}>
              <CardHeader className="pb-4 px-6 pt-6 border-b border-white/[0.06]">
                <div className="flex items-center justify-between gap-4">
                  <h2 className="text-[17px] font-light text-white tracking-tight leading-tight">Quote Summary</h2>
                  <span className="text-[10px] uppercase tracking-[0.12em] text-white/35">VEXOS</span>
                </div>
              </CardHeader>
              <CardContent className="px-6 py-2 text-sm">
                {/* Platform Setup */}
                <div className="py-4 border-b border-white/[0.06]">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-[14px] text-white font-medium tracking-tight">Platform Setup</span>
                    <span className="text-[15px] text-white font-semibold tabular-nums">${calculations.platformSubtotal.toLocaleString()}</span>
                  </div>
                  <div className="pl-1 space-y-1.5 text-[12px]">
                    <div className="flex justify-between"><span className="text-white/45">Base Platform</span><span className="text-white/65 tabular-nums">${calculations.baseSetup.toLocaleString()}</span></div>
                    {calculations.additionalBuildingsCost > 0 && (
                      <div className="flex justify-between"><span className="text-white/45">Add'l Buildings · {residentialBuildings - 1}</span><span className="text-white/65 tabular-nums">${calculations.additionalBuildingsCost.toLocaleString()}</span></div>
                    )}
                    {calculations.amenityCost > 0 && (
                      <div className="flex justify-between"><span className="text-white/45">Amenity Buildings · {amenityBuildings}</span><span className="text-white/65 tabular-nums">${calculations.amenityCost.toLocaleString()}</span></div>
                    )}
                    <div className="flex justify-between"><span className="text-white/45">Inventory · {residentialUnits} units</span><span className="text-white/65 tabular-nums">${calculations.unitsCost.toLocaleString()}</span></div>
                  </div>
                </div>

                {/* Exterior Modeling */}
                {exteriorCalc.exteriorModelingTotal > 0 && (
                  <div className="py-4 border-b border-white/[0.06]">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-[14px] text-white font-medium tracking-tight">Exterior Modeling</span>
                      <span className="text-[15px] text-white font-semibold tabular-nums">${exteriorCalc.exteriorModelingTotal.toLocaleString()}</span>
                    </div>
                    <div className="pl-1 space-y-1.5 text-[12px]">
                      {exteriorCalc.primaryStructureSubtotal > 0 && <div className="flex justify-between"><span className="text-white/45">Primary Structures · {exteriorCalc.totalUniqueStructures}</span><span className="text-white/65 tabular-nums">${exteriorCalc.primaryStructureSubtotal.toLocaleString()}</span></div>}
                      {exteriorCalc.replicationSubtotal > 0 && <div className="flex justify-between"><span className="text-white/45">Repeated Placements · {exteriorCalc.totalReplicatedPlacements}</span><span className="text-white/65 tabular-nums">${exteriorCalc.replicationSubtotal.toLocaleString()}</span></div>}
                      {exteriorCalc.siteEnvironmentCost > 0 && <div className="flex justify-between"><span className="text-white/45">Site Environment</span><span className="text-white/65 tabular-nums">${exteriorCalc.siteEnvironmentCost.toLocaleString()}</span></div>}
                      {exteriorCalc.realityCaptureCost > 0 && <div className="flex justify-between"><span className="text-white/45">Reality Capture</span><span className="text-white/65 tabular-nums">${exteriorCalc.realityCaptureCost.toLocaleString()}</span></div>}
                    </div>
                  </div>
                )}

                {/* Orbit Configuration */}
                {orbitCalc.orbitConfigurationTotal > 0 && (
                  <div className="py-4 border-b border-white/[0.06]">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-[14px] text-white font-medium tracking-tight">Orbit Configuration</span>
                      <span className="text-[15px] text-white font-semibold tabular-nums">${orbitCalc.orbitConfigurationTotal.toLocaleString()}</span>
                    </div>
                    <div className="pl-1 space-y-1.5 text-[12px]">
                      {orbitCalc.masterOrbitCost > 0 && <div className="flex justify-between"><span className="text-white/45">Suborbit</span><span className="text-white/65 tabular-nums">${orbitCalc.masterOrbitCost.toLocaleString()}</span></div>}
                      {orbitCalc.additionalOrbitDetails.map(orbit => (
                        <div key={orbit.id} className="flex justify-between">
                          <span className="text-white/45">{orbit.type === "custom" ? `Custom: ${orbit.customName || 'Unnamed'} · ×${orbit.quantity}` : `${orbit.typeLabel} · ×${orbit.quantity}`}</span>
                          <span className="text-white/65 tabular-nums">${orbit.subtotal.toLocaleString()}</span>
                        </div>
                      ))}
                      {orbitCalc.generalAreaOrbitCost > 0 && <div className="flex justify-between"><span className="text-white/45">General Area Orbit</span><span className="text-white/65 tabular-nums">${orbitCalc.generalAreaOrbitCost.toLocaleString()}</span></div>}
                      {orbitCalc.dayNightCost > 0 && <div className="flex justify-between"><span className="text-white/45">Day & Night Experience</span><span className="text-white/65 tabular-nums">${orbitCalc.dayNightCost.toLocaleString()}</span></div>}
                    </div>
                  </div>
                )}

                {/* Floorplate Development */}
                <div className="py-4 border-b border-white/[0.06]">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-[14px] text-white font-medium tracking-tight">Floorplate Development</span>
                    <span className="text-[15px] text-white font-semibold tabular-nums">${calculations.floorplateSubtotal.toLocaleString()}</span>
                  </div>
                  <div className="pl-1 space-y-1.5 text-[12px]">
                    <div className="flex justify-between"><span className="text-white/45">Unique · {uniqueFloorplates}</span><span className="text-white/65 tabular-nums">${calculations.uniqueFloorplateCost.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-white/45">Repeat · {repeatFloorplates}</span><span className="text-white/65 tabular-nums">${calculations.repeatFloorplateCost.toLocaleString()}</span></div>
                  </div>
                </div>

                {/* One-time Total */}
                <div className="py-4 border-b border-white/[0.08]">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[13px] text-white/70">One-time Total</span>
                    <span className="text-[17px] text-white font-light tabular-nums tracking-tight">${calculations.oneTimeTotal.toLocaleString()}</span>
                  </div>
                </div>

                {/* Platform License */}
                <div className="py-4 border-b border-white/[0.06]">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-[14px] text-white font-medium tracking-tight">Platform License</span>
                    <span className="text-[15px] text-white font-semibold tabular-nums">${calculations.totalRecurring.toLocaleString()}</span>
                  </div>
                  <div className="pl-1 space-y-1.5 text-[12px]">
                    <div className="flex justify-between"><span className="text-white/45">Monthly Rate</span><span className="text-white/65 tabular-nums">${calculations.monthlyLicense.toLocaleString()}<span className="text-white/35"> / mo</span></span></div>
                    <div className="flex justify-between"><span className="text-white/45">Agreement Term</span><span className="text-white/65">{licenseTerm} months</span></div>
                  </div>
                </div>

                {/* Estimated Production Timeline */}
                <TimelineEstimateWidget
                  deliverables={timelineDeliverables}
                  proposalTotal={calculations.grandTotal}
                  data-testid="vexos-timeline-widget"
                />

                {/* Grand Total */}
                <div className="mt-5 pt-5 border-t border-white/[0.08] space-y-3">
                  <div className="flex justify-between items-baseline">
                    <span className="text-[12px] text-white/55">Grand Total</span>
                    <span className="text-[17px] font-light text-white tabular-nums tracking-tight">${calculations.grandTotal.toLocaleString()}</span>
                  </div>

                  {/* Payment Preview */}
                  <div className="mt-4 p-4 bg-[#000000] border border-white/[0.08] rounded-[16px] space-y-2.5">
                    <p className="text-[10px] uppercase tracking-[0.1em] text-white/50 mb-1">Payment Preview</p>
                    <div className="flex justify-between text-[12px]"><span className="text-white/55">Due at signing · 50%</span><span className="text-white tabular-nums">${(calculations.oneTimeTotal * 0.5).toLocaleString()}</span></div>
                    <div className="flex justify-between text-[12px]"><span className="text-white/55">Due at milestone</span><span className="text-white tabular-nums">${(calculations.oneTimeTotal * 0.5).toLocaleString()}</span></div>
                    <div className="flex justify-between text-[12px] pt-2 border-t border-white/[0.06]"><span className="text-white/55">Monthly recurring</span><span className="text-white tabular-nums">${calculations.monthlyLicense.toLocaleString()}<span className="text-white/40"> / mo</span></span></div>
                  </div>
                </div>

                {coverage.statusType === "warning" && (
                  <div className="mt-3 flex items-center gap-2 text-[12px] text-amber-400 bg-amber-500/[0.08] border border-amber-500/20 p-2.5 rounded-[10px]">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{coverage.status}</span>
                  </div>
                )}

                <Button
                  className="w-full gap-2 mt-4 bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/[0.08] rounded-[12px] h-10"
                  variant="outline"
                  onClick={handleGenerateProposal}
                  disabled={generatingProposal || !quoteId || isNew}
                  data-testid="generate-proposal-btn"
                >
                  <FileText className="w-4 h-4" />
                  {generatingProposal ? "Generating..." : "Generate Proposal"}
                </Button>
                {isNew && (
                  <p className="text-[11px] text-white/40 text-center mt-2">
                    Save the quote first to generate a proposal
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Proposal Modal */}
      {proposalHtml && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Generated Proposal</h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePrintProposal}>
                  <FileText className="w-4 h-4 mr-2" />
                  Print / Save PDF
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setProposalHtml(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe
                srcDoc={proposalHtml}
                className="w-full h-full min-h-[600px]"
                title="Proposal Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
